import { useState, useEffect, useRef, useCallback } from 'react';
import { Input } from './input';
import { MapPin, Loader2 } from 'lucide-react';

// Australian state mapping from Google's address components
const STATE_MAPPING = {
  'New South Wales': 'NSW',
  'Victoria': 'VIC',
  'Queensland': 'QLD',
  'South Australia': 'SA',
  'Western Australia': 'WA',
  'Tasmania': 'TAS',
  'Northern Territory': 'NT',
  'Australian Capital Territory': 'ACT',
};

// Load Google Places script once
let googleScriptLoaded = false;
let googleScriptPromise = null;
let cachedApiKey = null;

// Add CSS to ensure pac-container appears above modal/sheet and is clickable
const ensurePacContainerStyle = () => {
  if (document.getElementById('pac-container-style')) return;
  const style = document.createElement('style');
  style.id = 'pac-container-style';
  style.textContent = `
    .pac-container {
      z-index: 99999 !important;
      pointer-events: auto !important;
      position: fixed !important;
    }
    .pac-item {
      cursor: pointer !important;
      pointer-events: auto !important;
    }
    .pac-item:hover {
      background-color: #f5f5f5 !important;
    }
  `;
  document.head.appendChild(style);
};

// Fetch API key from backend (with fallback to env var)
const getGooglePlacesApiKey = async () => {
  // Return cached key if available
  if (cachedApiKey) return cachedApiKey;
  
  // Try to fetch from backend first
  try {
    const API_URL = process.env.REACT_APP_BACKEND_URL;
    const response = await fetch(`${API_URL}/api/config/google-places`);
    if (response.ok) {
      const data = await response.json();
      if (data.configured && data.api_key) {
        cachedApiKey = data.api_key;
        return cachedApiKey;
      }
    }
  } catch (err) {
    console.warn('Failed to fetch Google Places API key from backend:', err);
  }
  
  // Fall back to environment variable
  cachedApiKey = process.env.REACT_APP_GOOGLE_PLACES_API_KEY;
  return cachedApiKey;
};

const loadGooglePlacesScript = async () => {
  if (googleScriptLoaded) {
    return Promise.resolve();
  }
  
  if (googleScriptPromise) {
    return googleScriptPromise;
  }
  
  googleScriptPromise = (async () => {
    const apiKey = await getGooglePlacesApiKey();
    if (!apiKey) {
      throw new Error('Google Places API key not configured');
    }
    
    return new Promise((resolve, reject) => {
      const script = document.createElement('script');
      script.src = `https://maps.googleapis.com/maps/api/js?key=${apiKey}&libraries=places`;
      script.async = true;
      script.defer = true;
      
      script.onload = () => {
        googleScriptLoaded = true;
        resolve();
      };
      
      script.onerror = () => {
        reject(new Error('Failed to load Google Places script'));
      };
      
      document.head.appendChild(script);
    });
  })();
  
  return googleScriptPromise;
};

/**
 * Google Places Autocomplete component for Australian addresses
 * 
 * @param {Object} props
 * @param {function} props.onPlaceSelect - Callback when a place is selected
 *   Receives: { street_address, suburb, state, postcode, formatted_address }
 * @param {string} props.placeholder - Input placeholder text
 * @param {string} props.value - Current input value (optional, for controlled mode)
 * @param {function} props.onChange - Input change handler (optional)
 * @param {string} props.className - Additional CSS classes
 */
export const GooglePlacesAutocomplete = ({
  onPlaceSelect,
  placeholder = "Start typing an address...",
  value: controlledValue,
  onChange: controlledOnChange,
  className = "",
  ...props
}) => {
  const [inputValue, setInputValue] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const inputRef = useRef(null);
  const autocompleteRef = useRef(null);
  
  // Use controlled or uncontrolled value
  const value = controlledValue !== undefined ? controlledValue : inputValue;
  
  // Helper to update value - handles both controlled and uncontrolled modes
  const updateValue = useCallback((newValue) => {
    if (controlledOnChange) {
      // Create a synthetic event for controlled components
      const syntheticEvent = {
        target: { value: newValue }
      };
      controlledOnChange(syntheticEvent);
    } else {
      setInputValue(newValue);
    }
  }, [controlledOnChange]);

  const handlePlaceSelect = useCallback(() => {
    if (!autocompleteRef.current) return;
    
    const place = autocompleteRef.current.getPlace();
    
    // If no address_components, the user may have just pressed Enter without selecting
    // In this case, we don't auto-fill but also don't throw an error
    if (!place || !place.address_components) {
      console.log('No place selected or address_components not available');
      return;
    }
    
    // Parse address components
    let streetNumber = '';
    let streetName = '';
    let suburb = '';
    let state = '';
    let postcode = '';
    
    place.address_components.forEach((component) => {
      const types = component.types;
      
      if (types.includes('street_number')) {
        streetNumber = component.long_name;
      }
      if (types.includes('route')) {
        streetName = component.long_name;
      }
      if (types.includes('locality') || types.includes('sublocality_level_1')) {
        suburb = component.long_name;
      }
      if (types.includes('administrative_area_level_1')) {
        // Convert full state name to abbreviation
        state = STATE_MAPPING[component.long_name] || component.short_name;
      }
      if (types.includes('postal_code')) {
        postcode = component.long_name;
      }
    });
    
    const street_address = streetNumber 
      ? `${streetNumber} ${streetName}`.trim() 
      : streetName;
    
    // Update input with formatted address
    updateValue(place.formatted_address || street_address);
    
    // Call the callback with parsed address
    if (onPlaceSelect) {
      onPlaceSelect({
        name: place.name || '', // Place/business name from Google
        street_address,
        suburb,
        state,
        postcode,
        formatted_address: place.formatted_address
      });
    }
  }, [onPlaceSelect, updateValue]);

  useEffect(() => {
    loadGooglePlacesScript()
      .then(() => {
        if (!inputRef.current) return;
        
        // Ensure pac-container z-index is high enough for modals
        ensurePacContainerStyle();
        
        // Create autocomplete instance
        autocompleteRef.current = new window.google.maps.places.Autocomplete(
          inputRef.current,
          {
            componentRestrictions: { country: 'au' }, // Restrict to Australia
            fields: ['address_components', 'formatted_address', 'geometry', 'name'],
            types: ['geocode', 'establishment'] // Return addresses and businesses/establishments
          }
        );
        
        // Add place_changed listener
        autocompleteRef.current.addListener('place_changed', handlePlaceSelect);
        
        // Prevent the pac-container clicks from being intercepted by modals
        // by stopping propagation on mousedown
        const observer = new MutationObserver(() => {
          const pacContainers = document.querySelectorAll('.pac-container');
          pacContainers.forEach(container => {
            if (!container.dataset.clickFixed) {
              container.dataset.clickFixed = 'true';
              container.addEventListener('mousedown', (e) => {
                e.stopPropagation();
              }, true);
              container.addEventListener('touchstart', (e) => {
                e.stopPropagation();
              }, true);
            }
          });
        });
        
        observer.observe(document.body, { childList: true, subtree: true });
        
        setIsLoading(false);
      })
      .catch((err) => {
        console.error('Google Places error:', err);
        setError(err.message);
        setIsLoading(false);
      });
    
    // Cleanup
    return () => {
      if (autocompleteRef.current) {
        window.google?.maps?.event?.clearInstanceListeners(autocompleteRef.current);
      }
    };
  }, [handlePlaceSelect]);

  const handleInputChange = (e) => {
    if (controlledOnChange) {
      // Pass the event object for controlled components expecting (e) => e.target.value
      controlledOnChange(e);
    } else {
      setInputValue(e.target.value);
    }
  };

  if (error) {
    // Fallback to regular input if Google Places fails
    return (
      <div className="relative">
        <Input
          value={value}
          onChange={handleInputChange}
          placeholder={placeholder}
          className={className}
          {...props}
        />
      </div>
    );
  }

  return (
    <div className="relative">
      <Input
        ref={inputRef}
        value={value}
        onChange={handleInputChange}
        placeholder={isLoading ? "Loading..." : placeholder}
        disabled={isLoading}
        className={`pl-9 ${className}`}
        {...props}
      />
      <div className="absolute left-3 top-1/2 -translate-y-1/2">
        {isLoading ? (
          <Loader2 className="h-4 w-4 text-muted-foreground animate-spin" />
        ) : (
          <MapPin className="h-4 w-4 text-muted-foreground" />
        )}
      </div>
    </div>
  );
};

export default GooglePlacesAutocomplete;
